import React from 'react';
import { Paper, Typography, Grid } from '@mui/material';
import { Control, Controller, useWatch } from 'react-hook-form';
import { CreatableEntityAssignmentSelector } from '../CreatableEntityAssignmentSelector';
import { SageReadinessWarning } from '../SageReadinessWarning';
import { EntityAssignment, ENTITY_ROLES } from '../../../types/workflow';
import type { CreateBatchFormData } from './validation';
import { salesAgentRepHint } from '../salesAgentHint';

interface EntityAssignmentsSectionProps {
  /** React Hook Form control object */
  control: Control<CreateBatchFormData>;
  /** Whether the form is currently submitting */
  disabled?: boolean;
}

/**
 * EntityAssignmentsSection Component
 *
 * Renders the entity assignment selectors in a 2x2 grid:
 * - Client (who will be invoiced) + Lab (who processes samples and receives purchase orders)
 * - Brand (optional Partner co-branding) + Sales Agent (optional; its Sage rep goes on the invoice)
 *
 * Shows Sage readiness warnings for Client and Lab when the selected
 * entity does not have Sage accounting details configured.
 *
 * Uses EntityAssignmentSelector for each field which supports
 * selecting from Farmers, Agents, or Labs (Lab selector is filtered to Labs only).
 */
export const EntityAssignmentsSection: React.FC<EntityAssignmentsSectionProps> = ({
  control,
  disabled = false,
}) => {
  const clientValue = useWatch({ control, name: 'billingEntity' }) as EntityAssignment | null;
  const supplierValue = useWatch({ control, name: 'supplier' }) as EntityAssignment | null;
  const salesAgentValue = useWatch({ control, name: 'salesAgent' }) as EntityAssignment | null | undefined;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle2" color="primary" gutterBottom>
        Entity Assignments
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="billingEntity"
            control={control}
            render={({ field }) => (
              <CreatableEntityAssignmentSelector
                value={field.value}
                onChange={field.onChange}
                label={ENTITY_ROLES.CLIENT}
                disabled={disabled}
                helperText="Who will be invoiced for this batch"
                allowCreate
              />
            )}
          />
          <SageReadinessWarning entity={clientValue} role={ENTITY_ROLES.CLIENT} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="supplier"
            control={control}
            render={({ field }) => (
              <CreatableEntityAssignmentSelector
                value={field.value}
                onChange={field.onChange}
                label={ENTITY_ROLES.SUPPLIER}
                disabled={disabled}
                helperText="Lab that processes the samples and receives purchase orders"
                allowedEntityTypes={['Lab']}
                allowCreate
              />
            )}
          />
          <SageReadinessWarning entity={supplierValue} role={ENTITY_ROLES.SUPPLIER} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="brand"
            control={control}
            render={({ field }) => (
              <CreatableEntityAssignmentSelector
                value={field.value}
                onChange={field.onChange}
                label={ENTITY_ROLES.BRAND}
                disabled={disabled}
                helperText="Optional — Partner whose branding appears on co-branded reports and PDFs"
                allowedEntityTypes={['Partner']}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="salesAgent"
            control={control}
            render={({ field }) => (
              <CreatableEntityAssignmentSelector
                value={field.value ?? null}
                onChange={field.onChange}
                label={ENTITY_ROLES.SALES_AGENT}
                disabled={disabled}
                helperText={salesAgentRepHint(salesAgentValue)}
                allowedEntityTypes={['Agent']}
                allowCreate
              />
            )}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default EntityAssignmentsSection;
